export type LiveConnectionState = 'connecting' | 'connected' | 'disconnected';

export interface ReconnectingSocketOptions {
  url: string;
  onMessage: (data: unknown) => void;
  onStateChange?: (state: LiveConnectionState) => void;
  initialBackoffMs?: number;
  maxBackoffMs?: number;
}

export interface ReconnectingSocketHandle {
  close: () => void;
  getState: () => LiveConnectionState;
  /** Resolves on the next successful open (or immediately if already connected). */
  whenConnected: (timeoutMs?: number) => Promise<void>;
}

export function connectReconnectingSocket(options: ReconnectingSocketOptions): ReconnectingSocketHandle {
  const initialBackoffMs = options.initialBackoffMs ?? 1000;
  const maxBackoffMs = options.maxBackoffMs ?? 30_000;

  let socket: WebSocket | null = null;
  let closed = false;
  let backoffMs = initialBackoffMs;
  let reconnectTimer: number | null = null;
  let state: LiveConnectionState = 'disconnected';
  const openWaiters: Array<{
    resolve: () => void;
    reject: (error: Error) => void;
    timer: number | null;
  }> = [];

  const setState = (next: LiveConnectionState): void => {
    if (state === next) {
      return;
    }

    state = next;
    options.onStateChange?.(next);
    if (next === 'connected') {
      for (const waiter of openWaiters.splice(0)) {
        if (waiter.timer !== null) {
          window.clearTimeout(waiter.timer);
        }
        waiter.resolve();
      }
    }
  };

  const scheduleReconnect = (): void => {
    if (closed) {
      return;
    }

    setState('disconnected');
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, backoffMs);
    backoffMs = Math.min(backoffMs * 2, maxBackoffMs);
  };

  const connect = (): void => {
    if (closed) {
      return;
    }

    setState('connecting');
    socket = new WebSocket(options.url);

    socket.addEventListener('open', () => {
      backoffMs = initialBackoffMs;
      setState('connected');
    });

    socket.addEventListener('message', (event) => {
      if (event.data === 'ping' || event.data === '{"type":"ping"}') {
        socket?.send('{"type":"pong"}');
        return;
      }

      try {
        const payload = JSON.parse(String(event.data)) as { type?: string };
        if (payload.type === 'ping') {
          socket?.send(JSON.stringify({ type: 'pong' }));
          return;
        }

        if (payload.type === 'pong') {
          return;
        }

        options.onMessage(payload);
      } catch {
        // ignore malformed frames
      }
    });

    socket.addEventListener('close', () => {
      socket = null;
      scheduleReconnect();
    });

    socket.addEventListener('error', () => {
      socket?.close();
    });
  };

  connect();

  return {
    close: () => {
      closed = true;
      if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer);
      }

      socket?.close();
      socket = null;
      for (const waiter of openWaiters.splice(0)) {
        if (waiter.timer !== null) {
          window.clearTimeout(waiter.timer);
        }
        waiter.reject(new Error('websocket_closed'));
      }
      setState('disconnected');
    },
    getState: () => state,
    whenConnected: (timeoutMs = 10_000) => {
      if (state === 'connected') {
        return Promise.resolve();
      }
      if (closed) {
        return Promise.reject(new Error('websocket_closed'));
      }
      return new Promise<void>((resolve, reject) => {
        const timer = window.setTimeout(() => {
          const index = openWaiters.findIndex((waiter) => waiter.resolve === resolve);
          if (index >= 0) {
            openWaiters.splice(index, 1);
          }
          reject(new Error('websocket_connect_timeout'));
        }, timeoutMs);
        openWaiters.push({ resolve, reject, timer });
      });
    },
  };
}

export function buildWebSocketUrl(path: string): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}${path}`;
}
